import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    ChevronLeft,
    Loader2,
    Info,
    RefreshCcw,
    User,
    ShoppingBag,
    ShieldCheck,
    Download,
    Send
} from 'lucide-react';
import { useToast } from '../hooks/useToast';
import {
    fetchAccountLedgerReport,
    fetchUtilAccounts,
    currencyCodeToSymbolMap,
    fetchAccountLedgerReportCSV,
    fetchStoresWhereStaffAssigned,
    fetchAllBankAccounts
} from '../apiHelper';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Checkbox from '../components/ui/Checkbox';
import DateTimeInput from "../components/ui/DateTimeInput";
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import Modal from '../components/ui/Modal';
import { useNavigate, useLocation } from 'react-router-dom';
import FastAPIClient from '../services/apiClient';

const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('en-US', {
        year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: true
    });
};

const formatCurrency = (amount, currencyCode) => {
    const symbol = currencyCodeToSymbolMap[currencyCode] || '$';
    const isNegative = amount < 0;
    const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(Math.abs(amount));
    return `${isNegative ? '-' : ''}${symbol}${formatted}`;
};

// const processAccountData = (rawLedger, selectedId) => {
//     if (!rawLedger || !selectedId || !rawLedger[selectedId]) return [];

//     const account = rawLedger[selectedId];
//     let runningBalance = 0;

//     const sortedTransactions = [...account.transactions].sort(
//         (a, b) => new Date(a.transaction_date) - new Date(b.transaction_date)
//     );

//     return sortedTransactions.map(t => {
//         const amount = parseFloat(t.amount);
//         if (t.posting_type === 'debit') runningBalance += amount;
//         else runningBalance -= amount;

//         return {
//             ...t,
//             account_name: account.account_name,
//             account_code: account.account_code,
//             cumulative_balance: runningBalance
//         };
//     });
// };

const processAccountData = (rawLedger, selectedId) => {
    if (!rawLedger || !selectedId || !rawLedger[selectedId]) return [];

    const account = rawLedger[selectedId];

    return account.transactions.map(t => ({
        ...t,
        account_name: account.account_name,
        account_code: account.account_code,
        cumulative_balance: parseFloat(t.running_balance)   // Trust backend value
    }));
};

const client = new FastAPIClient()

const AccountLedgerReport = () => {
    const { showToast } = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState(null);
    const [reportData, setReportData] = useState([]);
    const [parentAccounts, setParentAccounts] = useState([]);
    const [selectedAccountId, setSelectedAccountId] = useState('');
    const [orgName, setOrgName] = useState("");

    const [selectedCreatorId, setSelectedCreatorId] = useState('');
    const [creators, setCreators] = useState([]);

    const [loading, setLoading] = useState(false);
    const [csvLoading, setCsvLoading] = useState(false);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

    const [stores, setStores] = useState([]);
    const [selectedStoreId, setSelectedStoreId] = useState('');

    const selectedAccountLabel = useMemo(() => {
        const account = parentAccounts.find(a => a.value === selectedAccountId);
        return account ? account.label : '';
    }, [selectedAccountId, parentAccounts]);

    const isMainCashAccount = selectedAccountLabel.includes('Main Cash Control Account');

    const [isBankModalOpen, setIsBankModalOpen] = useState(false);
    const [bankAccountOptions, setBankAccountOptions] = useState([]);
    const [selectedBankId, setSelectedBankId] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [isOverrideEnabled, setIsOverrideEnabled] = useState(false);
    const [manualAmount, setManualAmount] = useState('');
    const [postingDateTime, setPostingDateTime] = useState(() => {
        const now = new Date();
        return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    });

    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (lcUser) {
            const parsedUser = JSON.parse(lcUser);
            setUser(parsedUser);
            loadUtilAccounts(parsedUser);
            loadStores(parsedUser); // Add this
        }
    }, []);

    useEffect(() => {
        const loadBanks = async () => {
            try {
                const fetchedAccounts = await fetchAllBankAccounts();
                const formattedOptions = fetchedAccounts.map(account => ({
                    label: `${account.name} (${account.account_id})`,
                    value: account.account_id
                }));
                setBankAccountOptions(formattedOptions);
            } catch (err) {
                showToast(err.response?.data?.detail || "Failed to load bank accounts", "error");
            }
        };
        loadBanks();
    }, []);

    const loadStores = async (currentUser) => {
        try {
            // Assuming fetchStoresWhereStaffAssigned is imported from your apiHelper
            const fetchedStores = await fetchStoresWhereStaffAssigned(currentUser);
            const mappedStores = fetchedStores.map(store => ({
                label: store.identifier,
                value: store.store_id,
            }));
            setStores(mappedStores);
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to load stores.", "error");
        }
    };

    const loadUtilAccounts = async (currentUser) => {
        setAccountsLoading(true);
        try {
            const fetchedUtilAccounts = await fetchUtilAccounts(currentUser);
            const mappedAccounts = fetchedUtilAccounts.map(account => ({
                value: account.account_id,
                label: `${account.name} (${account.account_code})`,
            }));

            setParentAccounts(mappedAccounts);

            const passedAccountId = location.state?.account_id;
            if (passedAccountId && mappedAccounts.some(a => a.value === passedAccountId)) {
                const today = new Date();
                const oneWeekAgo = new Date();
                oneWeekAgo.setDate(today.getDate() - 7);
                setEndDate(today.toISOString().split('T')[0]);
                setStartDate(oneWeekAgo.toISOString().split('T')[0]);
                setSelectedAccountId(passedAccountId);
            }
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to load accounts.", 'error');
        } finally {
            setAccountsLoading(false);
        }
    };

    const fetchReport = useCallback(async () => {
        if (!user || !selectedAccountId) return;
        setLoading(true);
        try {
            const params = {
                users_id: user.users_id,
                start_date: startDate,
                end_date: endDate,
                account_id: selectedAccountId,
                creator_id: selectedCreatorId || null,
                store_id: selectedStoreId || null,
            };
            // console.log(params)
            const response = await fetchAccountLedgerReport(params);
            console.log(response)
            setOrgName(response.organization_name);

            const processed = processAccountData(response.general_ledger, selectedAccountId);
            setReportData(processed);

            // --- AGGREGATE CREATORS ---
            const accountData = response.general_ledger[selectedAccountId];
            if (accountData?.transactions) {
                const creatorMap = new Map();
                accountData.transactions.forEach(t => {
                    if (t.creator_id && t.creator_name) {
                        creatorMap.set(t.creator_id, t.creator_name);
                    }
                });

                const creatorOptions = Array.from(creatorMap.entries()).map(([id, name]) => ({
                    value: id,
                    label: name
                }));
                setCreators(creatorOptions);
            }
            // --------------------------

        } catch (err) {
            console.error(err)
            showToast(err.response?.data?.detail || "Failed to load account ledger.", "error");
        } finally {
            setLoading(false);
        }
    }, [user, startDate, endDate, selectedAccountId, selectedCreatorId, selectedStoreId, showToast]);

    const handleDownloadCSV = async () => {
        if (!user || !selectedAccountId) return;
        setCsvLoading(true);

        try {
            const params = {
                users_id: user.users_id,
                start_date: startDate,
                end_date: endDate,
                account_id: selectedAccountId,
                creator_id: selectedCreatorId || null,
                store_id: selectedStoreId || null,
            };
            const csvContent = await fetchAccountLedgerReportCSV(params);
            console.log(csvContent)

            // Create a blob from the response content
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);

            // Create link and trigger download
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Ledger_Report_${startDate}_to_${endDate}.csv`);
            document.body.appendChild(link);
            link.click();

            // Clean up
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            showToast("CSV Downloaded successfully", "success");
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to download CSV.", "error");
        } finally {
            setCsvLoading(false);
        }
    };

    useEffect(() => {
        if (selectedAccountId) fetchReport();
    }, [selectedAccountId, selectedCreatorId, fetchReport]);

    // const totals = useMemo(() => {
    //     let debits = 0, credits = 0, cc = 'USD';
    //     if (reportData.length > 0) {
    //         cc = reportData[0].currency_code;
    //         reportData.forEach(t => {
    //             if (t.posting_type === 'debit') debits += t.amount;
    //             else credits += t.amount;
    //         });
    //     }
    //     return { debit: debits, credit: credits, currency: cc };
    // }, [reportData]);

    const totals = useMemo(() => {
        let salesDebits = 0;        // Total cash taken in from sales
        let bankedCredits = 0;      // Total cash already sent to bank
        let otherCredits = 0;       // Internal transfers (Petty cash, replenishment)
        let cc = 'NGN';

        if (reportData.length > 0) {
            cc = reportData[0].currency_code;
            reportData.forEach(t => {
                const amt = parseFloat(t.amount);
                if (t.posting_type === 'debit') {
                    // Focus on Sales-related debits
                    salesDebits += amt;
                } else {
                    if (t.transaction_type === 'CASH_DEPOSIT_TO_BANK') {
                        bankedCredits += amt;
                    } else {
                        // This is where your replenishment/petty cash movement goes
                        otherCredits += amt;
                    }
                }
            });
        }

        return {
            debit: salesDebits,
            credit: bankedCredits + otherCredits,
            currency: cc,
            // This is the logical balance of "Sales cash minus Banked cash"
            pendingBankAmount: salesDebits - bankedCredits
        };
    }, [reportData]);

    // const handleOpenBankModal = () => {
    //     if ((totals.debit - totals.credit) <= 0) {
    //         showToast("No positive cash balance to send.", "error");
    //         return;
    //     }
    //     setIsBankModalOpen(true);
    // };

    const handleOpenBankModal = () => {
        // If pendingBankAmount is positive, it means sales > banked_amounts
        if (totals.pendingBankAmount <= 0) {
            showToast("All recorded cash sales for this period have already been banked.", "info");
            return;
        }
        setIsBankModalOpen(true);
    };

    const handleConfirmSendToBank = async () => {
        if (!selectedBankId) {
            showToast("Please select a destination bank account.", "error");
            return;
        }

        const systemAmount = totals.debit - totals.credit;
        // Use manual amount if override is on, otherwise use system amount
        const finalAmount = isOverrideEnabled ? parseFloat(manualAmount) : systemAmount;

        if (isNaN(finalAmount) || finalAmount <= 0) {
            showToast("Please enter a valid amount greater than 0.", "error");
            return;
        }

        setIsSubmitting(true);

        const payload = {
            account_name: selectedAccountLabel,
            store_id: selectedStoreId || 0,
            cashier_id: selectedCreatorId || 0,
            system_calculated_amount: systemAmount,
            actual_amount_sent: finalAmount,
            is_manual_override: isOverrideEnabled,
            start_date: startDate,
            end_date: endDate,
            destination_bank_id: selectedBankId,
            posting_date: postingDateTime,
        };

        // console.log("--- FINAL SEND TO BANK PAYLOAD ---", payload);

        try {
            const response = await client.postData('/accounting/deposit-cash-on-hand', payload);
            showToast(response.msg || `Successfully initiated transfer of ${formatCurrency(finalAmount, totals.currency)}`, "success");
            setIsBankModalOpen(false);
            setIsOverrideEnabled(false); // Reset for next time
            setManualAmount('');

            // reload the ledger data
            await fetchReport();
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to process bank transfer.", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    // const handleSendToBank = () => {
    //     const netCashInDrawer = totals.debit - totals.credit;

    //     const payload = {
    //         account_name: selectedAccountLabel,
    //         store_id: selectedStoreId || 0,
    //         cashier_id: selectedCreatorId || 0,
    //         net_cash_in_drawer: netCashInDrawer,
    //         start_date: startDate,
    //         end_date: endDate,
    //     }

    //     console.log("--- Send to Bank Initialization ---");
    //     console.log("Account:", selectedAccountLabel);
    //     console.log("Store ID:", selectedStoreId || "All Stores"); // Added this line
    //     console.log("Cashier ID:", selectedCreatorId || "All Creators");
    //     console.log("Total Debits (Cash In):", totals.debit);
    //     console.log("Total Credits (Cash Out):", totals.credit);
    //     console.log("Net Cash in Drawer:", netCashInDrawer);
    //     console.log("Date Range:", `${startDate} to ${endDate}`);
    //     console.log("------------------------------------");

    //     showToast(`Processing bank transfer for ${formatCurrency(netCashInDrawer, totals.currency)}`, "info");
    // };


    return (
        <div className="p-4 md:p-8 bg-[#f8f9fa] min-h-screen relative text-slate-900">
            <div className="max-w-6xl mx-auto">
                <header className="mb-6">
                    <Button onClick={() => navigate(-1)} variant="ghost" className="p-0 h-auto mb-4 text-slate-500 hover:text-indigo-600 flex items-center">
                        <ChevronLeft size={16} /> <span className="text-xs uppercase tracking-wider font-bold">Back to Reports</span>
                    </Button>

                    <div className="mb-4">
                        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">{orgName || "Account Ledger"}</h1>
                        <p className="text-sm text-slate-500 font-medium font-mono">Ledger Extract</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 xl:gap-6 items-end bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                        {/* Account Select - Takes 1.5 columns for priority */}
                        <div className="md:col-span-1">
                            <SearchableSelectBox
                                label="Select Account"
                                options={parentAccounts}
                                value={selectedAccountId}
                                onChange={(e) => setSelectedAccountId(e.target.value)}
                                loading={accountsLoading}
                                placeholder="Account..."
                                containerClassName="mb-0"
                            />
                        </div>

                        <div className="md:col-span-1">
                            <SearchableSelectBox
                                label="Filter by Store"
                                options={stores}
                                value={selectedStoreId}
                                onChange={(e) => setSelectedStoreId(e.target.value)}
                                placeholder="All Stores"
                                isClearable={true}
                            />
                        </div>

                        {/* Creator Select - Takes 1 column */}
                        <div className="md:col-span-1">
                            <SearchableSelectBox
                                label="Filter by Creator"
                                options={creators}
                                value={selectedCreatorId}
                                onChange={(e) => setSelectedCreatorId(e.target.value)}
                                loading={loading}
                                placeholder="All Creators"
                                containerClassName="mb-0"
                                isClearable={true}
                            />
                        </div>

                        {/* Date Range - Takes 2 columns */}
                        <div className="md:col-span-2 xl:col-span-2 flex gap-2 mb-4">
                            <div className="flex-1">
                                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">From</label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="w-full text-xs border rounded-lg p-[9px] outline-none focus:ring-1 focus:ring-indigo-500"
                                />
                            </div>
                            <div className="flex-1">
                                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">To</label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="w-full text-xs border rounded-lg p-[9px] outline-none focus:ring-1 focus:ring-indigo-500"
                                />
                            </div>
                        </div>

                        {/* Action Buttons - Takes 1 column */}
                        <div className="flex md:justify-start xl:justify-end gap-2 md:col-span-2 lg:col-span-1 xl:col-span-1 mb-4">
                            <Button
                                onClick={handleDownloadCSV}
                                disabled={csvLoading || !selectedAccountId}
                                className="bg-emerald-600 text-white w-[42px] h-[42px] p-0 flex items-center justify-center rounded-lg shadow-md hover:bg-emerald-700 disabled:bg-slate-200"
                                title="Download CSV"
                            >
                                {csvLoading ? <Loader2 className="animate-spin" size={18} /> : <Download size={18} />}
                            </Button>

                            <Button
                                onClick={fetchReport}
                                disabled={loading || !selectedAccountId}
                                className="bg-indigo-600 text-white w-[42px] h-[42px] p-0 flex items-center justify-center rounded-lg shadow-md hover:bg-indigo-700"
                                title="Refresh Report"
                            >
                                {loading ? <Loader2 className="animate-spin" size={18} /> : <RefreshCcw size={18} />}
                            </Button>
                            {isMainCashAccount && (
                                <Button
                                    onClick={handleOpenBankModal}
                                    disabled={loading || !selectedAccountId || reportData.length == 0}
                                    className="bg-red-600 text-white w-[42px] h-[42px] p-0 flex items-center justify-center rounded-lg shadow-md hover:bg-red-700"
                                    title="Send to Bank"
                                >
                                    {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                                </Button>
                            )}
                        </div>
                    </div>
                </header>

                <Card className="overflow-hidden border-none shadow-xl rounded-2xl">
                    {loading ? (
                        <div className="h-64 flex flex-col items-center justify-center text-slate-400">
                            <Loader2 className="animate-spin mb-2" size={32} />
                            <p className="text-sm font-medium">Fetching Ledger...</p>
                        </div>
                    ) : reportData.length === 0 ? (
                        <div className="h-64 flex flex-col items-center justify-center text-slate-400">
                            <Info size={32} className="mb-2 opacity-20" />
                            <p className="text-sm font-medium">No transactions found.</p>
                        </div>
                    ) : (
                        <div className="bg-white">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-slate-50/80 border-b border-slate-100">
                                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">Date / Time</th>
                                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">Details & Origin</th>
                                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest text-right">Debit</th>
                                            <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest text-right">Credit</th>
                                            <th className="px-6 py-4 text-[11px] font-bold text-indigo-400 uppercase tracking-widest text-right bg-indigo-50/30">Balance</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-sm divide-y divide-slate-50">
                                        {reportData.map((t, i) => (
                                            <tr key={i} className="hover:bg-slate-50/50 transition-colors group">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-slate-700 text-xs font-semibold">{new Date(t.transaction_date).toLocaleDateString()}</div>
                                                    <div className="text-[10px] text-slate-400 uppercase font-medium">{new Date(t.transaction_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="font-bold text-slate-800 leading-tight mb-1">{t.description}</div>
                                                    <div className="flex flex-wrap gap-x-4 gap-y-1 items-center">
                                                        <div className="text-[10px] text-slate-400 font-mono flex items-center">
                                                            <span className="opacity-70 mr-1 font-bold tracking-tighter uppercase">REF:</span> {t.ref || '—'}
                                                        </div>
                                                        {t.customer_name && (
                                                            <div className="text-[10px] text-indigo-500 font-bold flex items-center">
                                                                <User size={10} className="mr-1" /> {t.customer_name}
                                                            </div>
                                                        )}
                                                        {t.store_identifier && (
                                                            <div className="text-[10px] text-emerald-600 font-bold flex items-center">
                                                                <ShoppingBag size={10} className="mr-1" /> {t.store_identifier}
                                                            </div>
                                                        )}
                                                        {t.creator_name && (
                                                            <div className="text-[10px] text-amber-600 font-bold flex items-center">
                                                                <ShieldCheck size={10} className="mr-1" /> {t.creator_name}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className={`px-6 py-4 text-right font-mono font-semibold ${t.posting_type === 'debit' ? 'text-slate-900' : 'text-slate-200'}`}>
                                                    {t.posting_type === 'debit' ? formatCurrency(t.amount, t.currency_code) : '—'}
                                                </td>
                                                <td className={`px-6 py-4 text-right font-mono font-semibold ${t.posting_type === 'credit' ? 'text-slate-900' : 'text-slate-200'}`}>
                                                    {t.posting_type === 'credit' ? formatCurrency(t.amount, t.currency_code) : '—'}
                                                </td>
                                                <td className="px-6 py-4 text-right font-mono font-bold text-indigo-900 bg-indigo-50/10 group-hover:bg-indigo-50/30 transition-colors">
                                                    <div className="flex flex-col items-end">
                                                        <span> {formatCurrency(t.running_balance, t.currency_code)} </span>
                                                        <span className="text-[11px] text-indigo-500 uppercase tracking-tighter">
                                                            {t.balance_type}
                                                        </span>
                                                    </div>
                                                    {/* {formatCurrency(t.cumulative_balance, t.currency_code)} */}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-slate-900 text-white border-t-4 border-indigo-500">
                                        <tr>
                                            <td colSpan="2" className="px-6 py-6 text-right text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Movement for Period</td>
                                            <td className="px-6 py-6 text-right font-mono font-bold text-lg">{formatCurrency(totals.debit, totals.currency)}</td>
                                            <td className="px-6 py-6 text-right font-mono font-bold text-lg text-indigo-400">{formatCurrency(totals.credit, totals.currency)}</td>
                                            <td className="px-6 py-6 text-right font-mono font-bold text-lg bg-slate-800 border-l border-slate-700">
                                                <div className="text-[9px] uppercase text-slate-500 mb-1">Net Change</div>
                                                {formatCurrency(totals.debit - totals.credit, totals.currency)}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    )}
                </Card>
            </div>
            <Modal
                isOpen={isBankModalOpen}
                onClose={() => setIsBankModalOpen(false)}
                title="Send Cash to Bank"
                size="md"
            >
                <div className="space-y-4">
                    {/* System Summary Card */}
                    <div className={`p-4 rounded-xl border transition-colors ${isOverrideEnabled ? 'bg-slate-50 border-slate-200 opacity-60' : 'bg-indigo-50 border-indigo-100'}`}>
                        <div className="flex justify-between items-center mb-2">
                            <p className="text-[10px] uppercase font-bold text-slate-500">Unbanked Sales Balance</p>
                            <span className="text-[10px] bg-indigo-200 text-indigo-700 px-2 py-0.5 rounded-full font-bold">Suggested</span>
                        </div>
                        <p className="text-2xl font-mono font-bold text-indigo-600">
                            {formatCurrency(totals.pendingBankAmount, totals.currency)}
                        </p>
                        <p className="text-[10px] text-slate-400 mt-1 italic">
                            (Total Sales: {formatCurrency(totals.debit, totals.currency)} - Already Banked: {formatCurrency(totals.credit - (totals.credit - totals.bankedCredits || 0), totals.currency)})
                        </p>
                    </div>

                    {/* Override Toggle */}
                    <Checkbox
                        label="Override system amount (Manual Count)"
                        checked={isOverrideEnabled}
                        onChange={(e) => setIsOverrideEnabled(e.target.checked)}
                        containerClassName="mb-2"
                    />

                    {/* Manual Input - Only visible/enabled when override is active */}
                    {isOverrideEnabled && (
                        <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                            <Input
                                type="number"
                                label="Actual Cash Amount"
                                placeholder="0.00"
                                value={manualAmount}
                                onChange={(e) => setManualAmount(e.target.value)}
                                className="border-amber-300 focus:ring-amber-500"
                                labelClassName="text-amber-700 font-bold"
                                step="0.01"
                                required
                            />
                            <p className="text-[11px] text-amber-600 flex items-center gap-1 mt-[-12px] mb-4 italic">
                                <Info size={12} /> Use this if the physical cash differs from the ledger.
                            </p>
                        </div>
                    )}

                    <DateTimeInput
                        label="Actual Date & Time of Posting"
                        value={postingDateTime}
                        onChange={(e) => setPostingDateTime(e.target.value)}
                        required
                        containerClassName="mb-4"
                    />

                    <SearchableSelectBox
                        label="Destination Bank Account"
                        options={bankAccountOptions}
                        value={selectedBankId}
                        onChange={(e) => setSelectedBankId(e.target.value)}
                        placeholder="Choose bank..."
                    />

                    <div className="flex gap-3 mt-6">
                        <Button
                            variant="ghost"
                            className="flex-1 border border-slate-200"
                            onClick={() => setIsBankModalOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            className={`flex-1 text-white ${isOverrideEnabled ? 'bg-amber-600 hover:bg-amber-700' : 'bg-red-600 hover:bg-red-700'}`}
                            onClick={handleConfirmSendToBank}
                            loading={isSubmitting}
                        >
                            Confirm & Send
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default AccountLedgerReport;